import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const BulkCorrectionModal = ({ 
  isOpen, 
  onClose, 
  selectedEntries = [], 
  onApply,
  loading = false 
}) => {
  const [correctionType, setCorrectionType] = useState('');
  const [adjustmentValue, setAdjustmentValue] = useState('');
  const [reason, setReason] = useState('');
  const [reasonCategory, setReasonCategory] = useState('');
  const [errors, setErrors] = useState({});

  const correctionTypeOptions = [
    { value: '', label: 'Korrekturtyp auswählen' },
    { value: 'time-shift', label: 'Zeitverschiebung (Minuten hinzufügen/subtrahieren)' },
    { value: 'round-times', label: 'Auf nächste Viertelstunde runden' },
    { value: 'dst-adjustment', label: 'Sommerzeit-Anpassung' },
    { value: 'approve-all', label: 'Alle Ausgewählten genehmigen' },
    { value: 'flag-all', label: 'Alle zur Überprüfung markieren' }
  ];

  const reasonCategoryOptions = [
    { value: '', label: 'Grundkategorie auswählen' },
    { value: 'system-maintenance', label: 'Systemwartung' },
    { value: 'dst-change', label: 'Sommerzeit' },
    { value: 'policy-update', label: 'Richtlinien-Update' },
    { value: 'payroll-correction', label: 'Lohnkorrektur' },
    { value: 'manager-directive', label: 'Manager-Anweisung' },
    { value: 'technical-issue', label: 'Technisches Problem' }
  ];


  const handleApply = () => {
    const newErrors = {};

    if (!correctionType) {
      newErrors.correctionType = 'Bitte wählen Sie einen Korrekturtyp aus';
    }

    if (!reason?.trim()) {
      newErrors.reason = 'Grund ist für Massenkorrekturen erforderlich';
    }

    if (!reasonCategory) {
      newErrors.reasonCategory = 'Bitte wählen Sie eine Grundkategorie aus';
    }

    // Validate adjustment value for specific correction types
    if (['time-shift', 'dst-adjustment']?.includes(correctionType) && !adjustmentValue) {
      newErrors.adjustmentValue = 'Anpassungswert ist für diesen Korrekturtyp erforderlich';
    }


    setErrors(newErrors);

    if (Object.keys(newErrors)?.length === 0) {
      const correctionData = {
        type: correctionType,
        value: adjustmentValue,
        reason,
        reasonCategory,
        entryIds: selectedEntries,
        appliedAt: new Date()?.toISOString(),
        appliedBy: 'current-user-id' // This would come from auth context
      };

      onApply(correctionData);
    }
  };

  const renderAdjustmentInput = () => {
    switch (correctionType) {
      case 'time-shift':
        return (
          <Input
            type="number"
            label="Minuten hinzufügen/abziehen"
            placeholder="z.B. -15 für 15 Minuten früher, +30 für 30 Minuten später"
            value={adjustmentValue}
            onChange={(e) => setAdjustmentValue(e?.target?.value)}
            error={errors?.adjustmentValue}
            description="Negative Werte zum Abziehen, positive Werte zum Hinzufügen von Zeit verwenden."
          />
        );

      case 'dst-adjustment':
        return (
          <Select
            label="Sommerzeit-Anpassung"
            options={[
              { value: '', label: 'Anpassung auswählen' },
              { value: 'spring-forward', label: 'Zeitumstellung auf Sommerzeit (+1 Stunde)' },
              { value: 'fall-back', label: 'Zeitumstellung auf Winterzeit (-1 Stunde)' }
            ]}
            value={adjustmentValue}
            onChange={setAdjustmentValue}
            error={errors?.adjustmentValue}
          />
        );


      case 'round-times':
        return (
          <div className="bg-muted/30 rounded-lg p-3">
            <p className="text-sm text-text-secondary">
              Dies rundet alle Kommen- und Gehen-Zeiten auf das nächste 15-Minuten-Intervall.
              Beispiel: 08:07 Uhr wird zu 08:00 Uhr, 17:23 Uhr wird zu 17:30 Uhr.
            </p>
          </div>
        );

      case 'approve-all':
        return (
          <div className="bg-success/10 border border-success/20 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <Icon name="Check" size={16} className="text-success" />
              <p className="text-sm text-success">
                Dies genehmigt alle {selectedEntries?.length} ausgewählten Einträge.
              </p>
            </div>
          </div>
        );

      case 'flag-all':
        return (
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <Icon name="Flag" size={16} className="text-warning" />
              <p className="text-sm text-warning">
                Dies markiert alle {selectedEntries?.length} ausgewählten Einträge zur Überprüfung.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getPreviewText = () => {
    if (!correctionType) return null;

    const count = selectedEntries?.length;
    switch (correctionType) {
      case 'time-shift':
        const direction = adjustmentValue > 0 ? 'nach hinten' : 'nach vorne';
        const minutes = Math.abs(adjustmentValue);
        return `${count} Einträge werden um ${minutes} Minuten ${direction} verschoben`;
      
      case 'round-times':
        return `Bei ${count} Einträgen werden die Zeiten auf die nächsten 15 Minuten gerundet`;
      
      case 'dst-adjustment':
        const dstText = adjustmentValue === 'spring-forward' ? 'um 1 Stunde vorgestellt' : 'um 1 Stunde zurückgestellt';
        return `Bei ${count} Einträgen wird die Zeit ${dstText}`;
      
      
      case 'approve-all':
        return `${count} Einträge werden genehmigt`;
      
      case 'flag-all':
        return `${count} Einträge werden zur Überprüfung markiert`;
      
      default:
        return `${count} Einträge sind von der Änderung betroffen`;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="Edit3" size={20} className="text-primary" />
            <div>
              <h2 className="text-lg font-semibold text-text-primary">
                Massenkorrektur
              </h2>
              <p className="text-sm text-text-secondary">
                Änderungen auf {selectedEntries?.length} ausgewählte Einträge anwenden
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
            className="text-text-secondary hover:text-text-primary"
          />
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <Select
            label="Korrekturtyp"
            options={correctionTypeOptions}
            value={correctionType}
            onChange={setCorrectionType}
            error={errors?.correctionType}
            required
          />

          {renderAdjustmentInput()}

          {/* Preview */}
          {getPreviewText() && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Icon name="Info" size={16} className="text-primary" />
                <p className="text-sm text-primary font-medium">
                  {getPreviewText()}
                </p>
              </div>
            </div>
          )}

          {/* Mandatory Documentation */}
          <div className="space-y-4 p-4 bg-warning/5 border border-warning/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-warning" />
              <span className="text-sm font-medium text-text-primary">
                Massenkorrektur-Dokumentation (Erforderlich)
              </span>
            </div>
            
            <Select
              label="Grundkategorie"
              options={reasonCategoryOptions}
              value={reasonCategory}
              onChange={setReasonCategory}
              error={errors?.reasonCategory}
              required
            />

            <Input
              label="Detaillierter Grund"
              type="text"
              placeholder="Geben Sie genaue Details an, warum diese Massenkorrektur notwendig ist..."
              value={reason}
              onChange={(e) => setReason(e?.target?.value)}
              error={errors?.reason}
              required
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-xs text-text-secondary">
            Alle Massenkorrekturen werden mit vollständigen Details im Audit-Trail protokolliert.
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={loading}
            >
              Abbrechen
            </Button>
            <Button
              variant="default"
              onClick={handleApply}
              loading={loading}
              iconName="CheckCheck"
              iconPosition="left"
            >
              Auf {selectedEntries?.length} Einträge anwenden
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkCorrectionModal;